// Body-section editor (PRINT_TEMPLATE_PLAN.md §4.3 + §6 Phase 6).
//
// Edits the `BodyConfig` slice: a sortable list of columns plus three
// toggles. Each column row exposes:
//   - drag handle (dnd-kit/sortable) for reorder
//   - key dropdown (only shows currently-unused keys + the row's own
//     current key, so duplicates are impossible)
//   - free-text label
//   - width slider (1..60, with running-total indicator)
//   - align segmented control
//   - format dropdown (plain / currency / date_short)
//   - remove button
//
// `group_by_day` + `show_day_subtotals` are diary-specific and hidden
// for receipt / invoice. Subtotals toggle is disabled when grouping
// is off (the renderer ignores it in that case).

import { useMemo, useState } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Plus, X } from 'lucide-react';

import { Button } from '../../../../components/ui/Button/Button';
import {
  getColumnKeysForType,
  type ColumnKeyDefinition,
} from '../variables';
import type {
  BodyConfig,
  ColumnDef,
  PrintTemplateType,
} from '../../../../types/api';
import styles from './BodyEditor.module.css';

const ALIGNMENTS: ReadonlyArray<{ value: 'left' | 'center' | 'right'; label: string }> = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];

const FORMATS: ReadonlyArray<{ value: 'plain' | 'currency' | 'date_short'; label: string }> = [
  { value: 'plain', label: 'Plain' },
  { value: 'currency', label: 'Currency (£)' },
  { value: 'date_short', label: 'Date (5 May 2026)' },
];

const MAX_COLUMNS = 12;
const MIN_WIDTH_PCT = 1;
const MAX_WIDTH_PCT = 60;

export interface BodyEditorProps {
  value: BodyConfig;
  onChange: (next: BodyConfig) => void;
  templateType: PrintTemplateType;
}

export function BodyEditor({
  value,
  onChange,
  templateType,
}: BodyEditorProps): JSX.Element {
  const allKeys = getColumnKeysForType(templateType);
  const usedKeys = useMemo(
    () => new Set(value.columns.map((c) => c.key)),
    [value.columns],
  );
  const [pickerOpen, setPickerOpen] = useState(false);

  const widthTotal = useMemo(
    () => value.columns.reduce((acc, c) => acc + c.width_pct, 0),
    [value.columns],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function updateColumns(next: ColumnDef[]) {
    onChange({ ...value, columns: next });
  }

  function setColumn(index: number, patch: Partial<ColumnDef>) {
    const cols = value.columns.map((c, i) => (i === index ? { ...c, ...patch } : c));
    updateColumns(cols);
  }

  function removeColumn(index: number) {
    updateColumns(value.columns.filter((_, i) => i !== index));
  }

  function addColumn(key: string) {
    const def = allKeys.find((k) => k.key === key);
    if (def === undefined) return;
    const remaining = Math.max(MIN_WIDTH_PCT, Math.min(20, 100 - widthTotal));
    const newCol: ColumnDef = {
      key: def.key,
      label: def.label,
      width_pct: remaining,
      align: def.format === 'currency' ? 'right' : 'left',
      format: def.format,
    };
    updateColumns([...value.columns, newCol]);
    setPickerOpen(false);
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over === null || active.id === over.id) return;
    const from = value.columns.findIndex((c) => c.key === active.id);
    const to = value.columns.findIndex((c) => c.key === over.id);
    if (from === -1 || to === -1) return;
    updateColumns(arrayMove([...value.columns], from, to));
  }

  const widthClass =
    widthTotal > 100
      ? styles.widthOver
      : widthTotal > 95
        ? styles.widthAmber
        : styles.widthOk;

  const isDiary = templateType === 'diary_results';
  const noKeysAvailable = allKeys.length === 0;
  const unusedKeys = allKeys.filter((k) => !usedKeys.has(k.key));
  const canAddMore = value.columns.length < MAX_COLUMNS && unusedKeys.length > 0;

  return (
    <section className={styles.section} aria-label="Body configuration">
      <header className={styles.head}>
        <h3 className={styles.title}>Body</h3>
        <div className={styles.headRight}>
          <span className={`${styles.widthBadge} ${widthClass}`} aria-live="polite">
            Total width: {widthTotal}%
          </span>
        </div>
      </header>

      {noKeysAvailable ? (
        <p className={styles.empty}>
          Body columns are not yet supported for this template type. Receipts
          and invoices will gain their column registries once those types
          migrate onto the print-template system.
        </p>
      ) : (
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={value.columns.map((c) => c.key)}
              strategy={verticalListSortingStrategy}
            >
              <ul className={styles.columnList}>
                {value.columns.map((col, idx) => (
                  <ColumnRow
                    key={col.key}
                    column={col}
                    onChange={(patch) => setColumn(idx, patch)}
                    onRemove={() => removeColumn(idx)}
                    allKeys={allKeys}
                    usedKeys={usedKeys}
                  />
                ))}
                {value.columns.length === 0 ? (
                  <li className={styles.empty}>
                    No columns yet — click "Add column" to get started.
                  </li>
                ) : null}
              </ul>
            </SortableContext>
          </DndContext>

          <div className={styles.addRow}>
            <Button
              variant="ghost"
              size="sm"
              icon={Plus}
              onClick={() => setPickerOpen((o) => !o)}
              disabled={!canAddMore}
              aria-expanded={pickerOpen}
            >
              {value.columns.length >= MAX_COLUMNS
                ? `Max ${MAX_COLUMNS} columns reached`
                : 'Add column'}
            </Button>
            {pickerOpen && unusedKeys.length > 0 ? (
              <div className={styles.picker} role="listbox" aria-label="Add column">
                {unusedKeys.map((k) => (
                  <button
                    key={k.key}
                    type="button"
                    role="option"
                    aria-selected={false}
                    className={styles.pickerItem}
                    onClick={() => addColumn(k.key)}
                  >
                    <span className={styles.pickerLabel}>{k.label}</span>
                    <code className={styles.pickerKey}>{k.key}</code>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </>
      )}

      <div className={styles.toggles}>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={value.show_zebra}
            onChange={(e) => onChange({ ...value, show_zebra: e.target.checked })}
          />
          <span>Zebra striping</span>
        </label>
        {isDiary ? (
          <>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={value.group_by_day}
                onChange={(e) =>
                  onChange({
                    ...value,
                    group_by_day: e.target.checked,
                    // Subtotals are meaningless without grouping.
                    show_day_subtotals: e.target.checked
                      ? value.show_day_subtotals
                      : false,
                  })
                }
              />
              <span>Group by day</span>
            </label>
            <label className={styles.toggle}>
              <input
                type="checkbox"
                checked={value.show_day_subtotals}
                disabled={!value.group_by_day}
                onChange={(e) =>
                  onChange({ ...value, show_day_subtotals: e.target.checked })
                }
              />
              <span>Show day subtotals</span>
            </label>
          </>
        ) : null}
      </div>
    </section>
  );
}

interface ColumnRowProps {
  column: ColumnDef;
  onChange: (patch: Partial<ColumnDef>) => void;
  onRemove: () => void;
  allKeys: ColumnKeyDefinition[];
  usedKeys: Set<string>;
}

function ColumnRow({
  column,
  onChange,
  onRemove,
  allKeys,
  usedKeys,
}: ColumnRowProps): JSX.Element {
  const sortable = useSortable({ id: column.key });
  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };
  // The key dropdown shows: this row's current key (always selectable)
  // plus every other unused key.
  const selectableKeys = allKeys.filter(
    (k) => k.key === column.key || !usedKeys.has(k.key),
  );

  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      className={`${styles.columnRow} ${sortable.isDragging ? styles.dragging : ''}`}
      aria-label={`Column ${column.label}`}
    >
      <button
        type="button"
        ref={sortable.setActivatorNodeRef}
        className={styles.grip}
        aria-label={`Drag to reorder ${column.label}`}
        {...sortable.attributes}
        {...sortable.listeners}
      >
        <GripVertical size={14} aria-hidden />
      </button>

      <select
        value={column.key}
        onChange={(e) => {
          const def = allKeys.find((k) => k.key === e.target.value);
          if (def === undefined) return;
          onChange({
            key: def.key,
            // If the row is still using the default label, swap it
            // for the new key's default. Otherwise keep the user's
            // custom label.
            label: column.label,
            format: def.format,
          });
        }}
        aria-label="Column key"
        className={styles.select}
      >
        {selectableKeys.map((k) => (
          <option key={k.key} value={k.key}>
            {k.label} ({k.key})
          </option>
        ))}
      </select>

      <input
        type="text"
        value={column.label}
        onChange={(e) => onChange({ label: e.target.value })}
        aria-label="Column label"
        className={styles.input}
        maxLength={80}
      />

      <label className={styles.widthLabel}>
        <input
          type="range"
          min={MIN_WIDTH_PCT}
          max={MAX_WIDTH_PCT}
          step={1}
          value={column.width_pct}
          onChange={(e) =>
            onChange({ width_pct: Math.round(Number(e.target.value)) })
          }
          aria-label="Column width percentage"
          className={styles.range}
        />
        <span className={styles.widthValue}>{column.width_pct}%</span>
      </label>

      <div className={styles.segmented} role="group" aria-label="Column alignment">
        {ALIGNMENTS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={
              column.align === opt.value
                ? `${styles.segItem} ${styles.segItemActive}`
                : styles.segItem
            }
            aria-pressed={column.align === opt.value}
            onClick={() => onChange({ align: opt.value })}
          >
            {opt.label[0]}
          </button>
        ))}
      </div>

      <select
        value={column.format ?? 'plain'}
        onChange={(e) =>
          onChange({
            format: e.target.value as 'plain' | 'currency' | 'date_short',
          })
        }
        aria-label="Column format"
        className={styles.select}
      >
        {FORMATS.map((f) => (
          <option key={f.value} value={f.value}>
            {f.label}
          </option>
        ))}
      </select>

      <Button
        variant="icon"
        size="sm"
        icon={X}
        onClick={onRemove}
        aria-label={`Remove ${column.label} column`}
      />
    </li>
  );
}
